import React, { createContext, useContext, useState, useEffect } from 'react';
import { Operator, Order, Deposit, Buyback, OperatorId, PackageItem, AirtimeDenomination, DashboardStats, RateSchedule, DashboardFilter, DashboardTimeframe } from '../types';
import { useAuth } from './AuthContext';
import { api } from '../lib/api';

export interface AdminContextType {
  operators: Operator[];
  deposits: Deposit[];
  orders: Order[];
  buybacks: Buyback[];
  packages: PackageItem[];
  rates: RateSchedule[];
  airtimeDenominations: AirtimeDenomination[];
  dashboardStats: DashboardStats | null;
  dashboardDays: DashboardTimeframe;
  dashboardFilter: DashboardFilter;
  setDashboardDays: (days: DashboardTimeframe) => void;
  setDashboardFilter: (filter: DashboardFilter | ((prev: DashboardFilter) => DashboardFilter)) => void;
  loadDashboardStats: (filter?: DashboardFilter | DashboardTimeframe) => Promise<void>;
  loading: boolean;
  actionLoading: boolean;
  pendingOrdersCount: number;
  pendingDepositsCount: number;
  pendingBuybacksCount: number;
  refresh: () => Promise<void>;
  updateOperatorStatus: (operatorId: OperatorId, status: 'open' | 'closed_daily_limit' | 'closed_other', reason?: string) => Promise<void>;
  claimOrder: (orderId: string) => Promise<void>;
  confirmDelivered: (orderId: string, deliveryRef: string, deliveryNote?: string) => Promise<void>;
  approveDeposit: (depositId: string) => Promise<void>;
  rejectDeposit: (depositId: string, reason: string) => Promise<void>;
  settleBuyback: (buybackId: string, payoutTxRef?: string) => Promise<void>;
  togglePackageStock: (packageId: string) => Promise<void>;
  updatePackagePrice: (packageId: string, sellingPrice: number, costPrice?: number) => Promise<void>;
  updatePackage: (packageId: string, updates: Partial<PackageItem>) => Promise<void>;
  createPackage: (pkg: Partial<PackageItem>) => Promise<void>;
  updateAirtimeRate: (operatorId: OperatorId, ratePercentage: number, direction?: 'buy' | 'sell') => Promise<void>;
  saveAirtimeDenomination: (denomination: Partial<AirtimeDenomination>) => Promise<void>;
  toggleAirtimeDenomination: (id: string) => Promise<void>;
  deleteAirtimeDenomination: (id: string) => Promise<void>;
  batchUpdateAirtimeRates: (operatorId: OperatorId, ratePercentage: number, costPercentage?: number) => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin } = useAuth();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [buybacks, setBuybacks] = useState<Buyback[]>([]);
  const [packages, setPackages] = useState<PackageItem[]>([]);
  const [rates, setRates] = useState<RateSchedule[]>([]);
  const [airtimeDenominations, setAirtimeDenominations] = useState<AirtimeDenomination[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [dashboardFilter, setDashboardFilter] = useState<DashboardFilter>({ days: 30 });

  const dashboardDays = dashboardFilter.days ?? 30;
  const setDashboardDays = (days: DashboardTimeframe) => {
    setDashboardFilter({ days });
  };

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const loadDashboardStats = async (filterParam?: DashboardFilter | DashboardTimeframe) => {
    const targetFilter = filterParam !== undefined ? filterParam : dashboardFilter;
    try {
      const stats = await api.getAdminDashboard(targetFilter);
      setDashboardStats(stats);
    } catch (err) {
      console.error('Failed to load dashboard stats', err);
    }
  };

  const loadAdminData = async () => {
    if (!isAdmin) return;
    try {
      const [ops, queue, pkgs, stats, rts, dens] = await Promise.all([
        api.getOperators(),
        api.getAdminQueue(),
        api.getAdminPackages(),
        api.getAdminDashboard(dashboardFilter),
        api.getRates().catch(() => []),
        api.getAdminAirtimeDenominations().catch(() => []),
      ]);
      setOperators(ops);
      setDeposits(queue.deposits || []);
      setOrders(queue.orders || []);
      setBuybacks(queue.buybacks || []);
      setPackages(pkgs || []);
      setDashboardStats(stats);
      setRates(rts || []);
      setAirtimeDenominations(dens || []);
    } catch (err) {
      console.error('Failed to load admin operations data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadAdminData();
      const interval = setInterval(loadAdminData, 10000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const updateOperatorStatus = async (
    operatorId: OperatorId,
    status: 'open' | 'closed_daily_limit' | 'closed_other',
    reason?: string
  ) => {
    setActionLoading(true);
    try {
      await api.updateOperatorPackageStatus(operatorId, status, reason);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const claimOrder = async (orderId: string) => {
    setActionLoading(true);
    try {
      await api.claimOrder(orderId);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelivered = async (orderId: string, deliveryRef: string, deliveryNote?: string) => {
    setActionLoading(true);
    try {
      await api.confirmDelivered(orderId, deliveryRef, deliveryNote);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const approveDeposit = async (depositId: string) => {
    setActionLoading(true);
    try {
      await api.approveDeposit(depositId);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const rejectDeposit = async (depositId: string, reason: string) => {
    setActionLoading(true);
    try {
      await api.rejectDeposit(depositId, reason);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const settleBuyback = async (buybackId: string, payoutTxRef?: string) => {
    setActionLoading(true);
    try {
      await api.settleBuyback(buybackId, payoutTxRef);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const togglePackageStock = async (packageId: string) => {
    try {
      const updated = await api.togglePackageStock(packageId);
      setPackages((prev) =>
        prev.map((p) => (p.id === packageId ? { ...p, is_active: updated.is_active } : p))
      );
    } catch (err: any) {
      alert(err.message || 'Failed to toggle package stock');
    }
  };

  const updatePackage = async (packageId: string, updates: Partial<PackageItem>) => {
    setActionLoading(true);
    try {
      const updated = await api.updatePackage(packageId, updates);
      setPackages((prev) =>
        prev.map((p) => (p.id === packageId ? { ...p, ...updated } : p))
      );
    } finally {
      setActionLoading(false);
    }
  };

  const updatePackagePrice = async (packageId: string, sellingPrice: number, costPrice?: number) => {
    await updatePackage(packageId, {
      selling_price: sellingPrice,
      ...(costPrice !== undefined ? { cost_price: costPrice } : {}),
    });
  };

  const createPackage = async (pkg: Partial<PackageItem>) => {
    setActionLoading(true);
    try {
      await api.createPackage(pkg);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const updateAirtimeRate = async (operatorId: OperatorId, ratePercentage: number, direction: 'buy' | 'sell' = 'buy') => {
    setActionLoading(true);
    try {
      await api.updateAirtimeRate(operatorId, ratePercentage, direction);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const saveAirtimeDenomination = async (denomination: Partial<AirtimeDenomination>) => {
    setActionLoading(true);
    try {
      await api.saveAirtimeDenomination(denomination);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const toggleAirtimeDenomination = async (id: string) => {
    try {
      const updated = await api.toggleAirtimeDenomination(id);
      setAirtimeDenominations((prev) =>
        prev.map((d) => (d.id === id ? { ...d, is_active: updated.is_active } : d))
      );
    } catch (err: any) {
      alert(err.message || 'Failed to toggle denomination');
    }
  };

  const deleteAirtimeDenomination = async (id: string) => {
    setActionLoading(true);
    try {
      await api.deleteAirtimeDenomination(id);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const batchUpdateAirtimeRates = async (operatorId: OperatorId, ratePercentage: number, costPercentage?: number) => {
    setActionLoading(true);
    try {
      await api.batchUpdateAirtimeRates(operatorId, ratePercentage, costPercentage);
      await loadAdminData();
    } finally {
      setActionLoading(false);
    }
  };

  const pendingOrdersCount = orders.filter(
    (o) => o.fulfillment_status === 'queued' || o.fulfillment_status === 'processing' || (!o.claimed_by && o.fulfillment_status !== 'completed' && o.fulfillment_status !== 'unable_to_deliver')
  ).length;

  const pendingDepositsCount = deposits.filter(
    (d) => d.status === 'pending' || d.status === 'under_review'
  ).length;

  const pendingBuybacksCount = buybacks.filter(
    (b) => b.status === 'awaiting_transfer' || b.status === 'under_review' || b.status === 'receipt_verified'
  ).length;

  useEffect(() => {
    if (isAdmin && !loading) {
      loadDashboardStats(dashboardFilter);
    }
  }, [dashboardFilter]);

  return (
    <AdminContext.Provider
      value={{
        operators,
        deposits,
        orders,
        buybacks,
        packages,
        rates,
        airtimeDenominations,
        dashboardStats,
        dashboardDays,
        dashboardFilter,
        setDashboardDays,
        setDashboardFilter,
        loadDashboardStats,
        loading,
        actionLoading,
        pendingOrdersCount,
        pendingDepositsCount,
        pendingBuybacksCount,
        refresh: loadAdminData,
        updateOperatorStatus,
        claimOrder,
        confirmDelivered,
        approveDeposit,
        rejectDeposit,
        settleBuyback,
        togglePackageStock,
        updatePackagePrice,
        updatePackage,
        createPackage,
        updateAirtimeRate,
        saveAirtimeDenomination,
        toggleAirtimeDenomination,
        deleteAirtimeDenomination,
        batchUpdateAirtimeRates,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};
